import HoursDisplay from 'components/util/HoursDisplay.tsx';
import MoneyDisplay from 'components/util/MoneyDisplay.tsx';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { FinancialDataDto } from 'types/other';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations.ts';

type StatCardProps = {
    /** Classes for the outlined card. */
    className?: string;
    /** Classes for its padded content. */
    contentClassName?: string;
};

export function StatCard({ className, contentClassName, children }: StatCardProps & { children: React.ReactNode }) {
    return (
        <div className={cn('border-divider bg-paper text-fg rounded-[4px] border', className)}>
            <div className={cn('p-5', contentClassName)}>{children}</div>
        </div>
    );
}

export function StatValue({ className, children }: { className?: string; children: React.ReactNode }) {
    // The new design's figure is the `figure` step; classic keeps its own size and line.
    return (
        <p className={cn('new:text-figure new:leading-[1.2] m-0 text-2xl leading-[1.375rem] font-semibold', className)}>
            {children}
        </p>
    );
}

export function StatFootnote({ children }: { children: React.ReactNode }) {
    return (
        <div className="mt-4">
            <span className="text-caption text-fg-secondary">{children}</span>
        </div>
    );
}

type TotalHoursProps = StatCardProps & {
    financialData?: FinancialDataDto;
};

export const TotalHours: React.FC<TotalHoursProps> = ({ financialData, ...card }) => {
    return (
        <StatCard {...card}>
            <FormattedMessage id="stats.uninvoiced-hours" />
            <StatValue>
                <HoursDisplay value={financialData?.uninvoicedBilledTimeInHours} />
            </StatValue>
            <StatFootnote>
                {t('stats.total-invoiced-billable-time')}{' '}
                <HoursDisplay value={financialData?.invoicedBilledTimeInHours} />
            </StatFootnote>
        </StatCard>
    );
};

type TotalAmountsProps = StatCardProps & {
    financialData?: FinancialDataDto;
    currency?: string;
};

export const TotalAmounts: React.FC<TotalAmountsProps> = ({ financialData, currency, ...card }) => {
    return (
        <StatCard {...card}>
            <FormattedMessage id="stats.uninvoiced-timecards-amount" />
            <StatValue>
                <MoneyDisplay amount={financialData?.uninvoicedFees ?? 0} currency={currency} />
            </StatValue>
            <StatFootnote>
                {t('stats.total-invoiced-amount')}{' '}
                <MoneyDisplay amount={financialData?.invoicedFees ?? 0} currency={currency} />
            </StatFootnote>
        </StatCard>
    );
};

type TotalTimecardsProps = StatCardProps & {
    financialData?: FinancialDataDto;
    skip?: boolean;
};

export const TotalTimecards: React.FC<TotalTimecardsProps> = ({ financialData, skip, ...card }) => {
    if (skip) {
        return null;
    }

    return (
        <StatCard {...card}>
            {t('stats.uninvoiced-timecards')}
            <StatValue>{financialData?.uninvoicedTimecardCount ?? 0}</StatValue>
        </StatCard>
    );
};

type TotalExpenseAmountsProps = StatCardProps & {
    financialData?: FinancialDataDto;
    uninvoicedExpensesAmount?: number;
    currency?: string;
    skip?: boolean;
};

export const TotalExpenseAmounts: React.FC<TotalExpenseAmountsProps> = ({
    financialData,
    uninvoicedExpensesAmount,
    currency,
    skip,
    ...card
}) => {
    if (skip) {
        return null;
    }

    return (
        <StatCard {...card}>
            <FormattedMessage id="stats.uninvoiced-expenses-amount" />
            <StatValue>
                <MoneyDisplay amount={uninvoicedExpensesAmount ?? 0} currency={currency} />
            </StatValue>
            <StatFootnote>
                {t('stats.total-invoiced-amount')}{' '}
                <MoneyDisplay amount={financialData?.invoicedExpenseFees ?? 0} currency={currency} />
            </StatFootnote>
        </StatCard>
    );
};

type TotalExpensesProps = StatCardProps & {
    financialData?: FinancialDataDto;
    skip?: boolean;
};

export const TotalExpenses: React.FC<TotalExpensesProps> = ({ financialData, skip, ...card }) => {
    if (skip) {
        return null;
    }

    return (
        <StatCard {...card}>
            {t('stats.uninvoiced-expenses-count')}
            <StatValue>{financialData?.uninvoicedExpenseCount ?? 0}</StatValue>
        </StatCard>
    );
};
